import { Injectable, NgZone } from '@angular/core';
import { Feature, GeoJsonProperties, Geometry, Point, Polygon, Position } from 'geojson';
import { BehaviorSubject } from 'rxjs';
import { CanvasMarkerIcon, CircleMarkerSizeMap, MarkerShape, MarkerSize, MarkerSizeMap } from '../config/map-config';
import DrawingManagerOptions = google.maps.drawing.DrawingManagerOptions;

@Injectable()
export class GoogleMapService {
  private markers = [];
  private polygons = [];
  private polylines = [];
  private drawingManager: google.maps.drawing.DrawingManager;
  private wmsLayer?: google.maps.ImageMapType;

  private tilesLoadedListener: google.maps.MapsEventListener | null = null;
  private isWsmLayerTilesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isWsmLayerTilesLoading$ = this.isWsmLayerTilesLoading.asObservable();

  private readonly GEOPORTAL_TILES_URL: string = 'https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow';

  constructor(private readonly ngZone: NgZone) {}

  public isGeoJSONPointFeature(feature: Feature): feature is Feature<Point> {
    return feature.geometry.type === 'Point';
  }

  public isGeoJSONPolygonFeature(feature: Feature): feature is Feature<Polygon> {
    return feature.geometry.type == 'Polygon';
  }

  setMapOptions(map: google.maps.Map, options: google.maps.MapOptions) {
    map.setOptions(options);
  }

  createMarker(map: google.maps.Map, options: google.maps.ReadonlyMarkerOptions): google.maps.Marker {
    const marker = new google.maps.Marker(options);
    marker.setMap(map);
    this.markers.push(marker);
    return marker;
  }

  createSingleMarker(map: google.maps.Map, options: google.maps.MarkerOptions): google.maps.Marker {
    this.markers.forEach(x => {
      x.setMap(null);
    });
    const marker = new google.maps.Marker(options);
    marker.setMap(map);
    this.markers.push(marker);
    return marker;
  }

  createPolygon(map: google.maps.Map, options: google.maps.PolygonOptions): google.maps.Polygon {
    const polygon = new google.maps.Polygon(options);
    polygon.setMap(map);

    this.polygons.push(polygon);
    return polygon;
  }

  clearPolygons() {
    this.polygons.forEach(polygon => this.removePolygon(polygon));
    this.polygons = [];
  }

  clearMarkers() {
    this.markers.forEach(marker => this.removeMarker(marker));
    this.markers = [];
  }

  clearPolylines() {
    this.polylines.forEach(polyline => this.removePolyline(polyline));
    this.polylines = [];
  }

  removePolygon(polygon: google.maps.Polygon) {
    polygon.setMap(null);
  }

  removeMarker(marker: google.maps.Marker) {
    marker.setMap(null);
  }

  removePolyline(polyline: google.maps.Polyline) {
    polyline.setMap(null);
  }

  setZoom(map: google.maps.Map, zoomValue = 13): void {
    map.setZoom(zoomValue);
  }

  centerMap(map: google.maps.Map, value: google.maps.LatLng): void {
    map.setCenter(value);
  }

  fitBounds(map: google.maps.Map, bounds: google.maps.LatLngBounds): void {
    map.fitBounds(bounds);
    map.panToBounds(bounds);
  }

  overlayTiles(map: google.maps.Map, imageMapType: google.maps.ImageMapType) {
    map.overlayMapTypes.push(imageMapType);
  }

  clearMapTypes(map: google.maps.Map) {
    map.overlayMapTypes.clear();
  }

  calculatePolygonArea(paths: google.maps.MVCArray<google.maps.LatLng> | google.maps.LatLng[]): number {
    const meterToHa = 10000;
    const areaValue = google.maps.geometry.spherical.computeArea(paths) / meterToHa;
    return areaValue;
  }

  public calculatePolygonAreaInHa(polygon: google.maps.Polygon): number {
    const areaInHa = google.maps.geometry.spherical.computeArea(polygon.getPath());
    const roundedAreaInHa = Math.round(((areaInHa + Number.EPSILON) / 10000) * 100) / 100;
    return roundedAreaInHa;
  }

  createDrawingManager(map: google.maps.Map, options: DrawingManagerOptions): google.maps.drawing.DrawingManager {
    if (this.drawingManager) {
      this.drawingManager.setMap(null);
    }
    this.drawingManager = new google.maps.drawing.DrawingManager(options);
    this.drawingManager.setMap(map);
    return this.drawingManager;
  }

  public createGeoJSONFeatureFromPolygon(polygon: google.maps.Polygon, properties?: GeoJsonProperties): Feature<Polygon> {
    const paths = polygon.getPaths().getArray();
    const coordinates = paths[0].getArray().map(latLng => [latLng.lng(), latLng.lat()]);

    const geoJSONPolygon: Polygon = {
      type: 'Polygon',
      coordinates: [coordinates]
    };

    const feature: Feature<Polygon> = {
      type: 'Feature',
      geometry: geoJSONPolygon,
      properties: { ...properties }
    };

    return feature;
  }

  public createPolygonFromGeoJSONFeature(
    map: google.maps.Map,
    feature: Feature<Polygon>,
    polygonOptions?: google.maps.PolygonOptions
  ): google.maps.Polygon {
    const polygon = feature.geometry;
    const coordinates = polygon.coordinates;
    const paths = coordinates.map(ring => ring.map(coord => new google.maps.LatLng(coord[1], coord[0])));

    const defaultColor = '#ffff00';

    const defaultPolygonOptions: google.maps.PolygonOptions = {
      paths: paths,
      strokeColor: polygonOptions && polygonOptions.strokeColor ? polygonOptions.strokeColor : defaultColor,
      strokeOpacity: polygonOptions && polygonOptions.strokeOpacity ? polygonOptions.strokeOpacity : 0.8,
      strokeWeight: polygonOptions && polygonOptions.strokeWeight ? polygonOptions.strokeWeight : 2,
      fillColor: polygonOptions && polygonOptions.fillColor ? polygonOptions.fillColor : defaultColor,
      fillOpacity: polygonOptions && polygonOptions.fillOpacity ? polygonOptions.fillOpacity : 0.35,
      zIndex: 100,
      map: map
    };

    const finalPolygonOptions = { ...defaultPolygonOptions, ...polygonOptions };

    return new google.maps.Polygon(finalPolygonOptions);
  }

  public mapPolygonGeometryToPaths(locationGeometry: Geometry): google.maps.LatLng[] | google.maps.LatLng[][] {
    if (locationGeometry.type === 'Polygon') {
      return locationGeometry.coordinates.map(coordinate => {
        return coordinate.map(x => {
          return new google.maps.LatLng({
            lat: x[1],
            lng: x[0]
          });
        });
      });
    }

    return [];
  }

  public mapFeatureToPoint(features: Array<Feature>): { lat: number; lng: number } {
    for (const feature of features) {
      if (feature.geometry.type === 'Point') {
        const position = feature.geometry.coordinates as Position;
        return { lat: position[1], lng: position[0] };
      }
      throw new Error('Unsupported feature type');
    }

    throw new Error('Empty array');
  }

  public mapPointGeometryToPaths(locationGeometry: Geometry): google.maps.LatLng {
    if (locationGeometry.type === 'Point') {
      return new google.maps.LatLng(locationGeometry.coordinates[1], locationGeometry.coordinates[0]);
    }

    return null;
  }

  public mapPolygonToFeature(polygon: google.maps.Polygon): Feature<Polygon> {
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: this.getPolygonCoordinates(polygon)
      },
      properties: {}
    };
  }

  public mapMarkerToFeature(marker: google.maps.Marker): Feature<Point> {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [marker.getPosition().lng(), marker.getPosition().lat()]
      },
      properties: {}
    };
  }

  public mapPointToFeature(lat: number, long: number): Feature<Point> {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [long, lat]
      },
      properties: {}
    };
  }

  public createFeaturePolygonFromCoordinates(coordinates: Position[][]): Feature<Polygon> {
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: coordinates
      },
      properties: {}
    };
  }

  public getCenterPoint(overlay: google.maps.Marker | google.maps.Polygon): { lat: number; lng: number } {
    if (overlay instanceof google.maps.Marker) {
      const position = overlay.getPosition();
      return { lat: position.lat(), lng: position.lng() };
    }

    if (overlay instanceof google.maps.Polygon) {
      const bounds = new google.maps.LatLngBounds();
      overlay.getPath().forEach(point => bounds.extend(point));
      const center = bounds.getCenter();
      return { lat: center.lat(), lng: center.lng() };
    }

    throw new Error('Unsupported overlay type');
  }

  public getCenterPointFromFeatures(features: Array<Feature>): { lat: number; lng: number } | null {
    for (const feature of features) {
      if (feature.geometry.type === 'Point') {
        const position = feature.geometry.coordinates as Position;
        return { lat: position[1], lng: position[0] };
      }
    }

    for (const feature of features) {
      if (feature.geometry.type === 'Polygon') {
        const polygon = feature.geometry.coordinates as Position[][];
        let totalX = 0,
          totalY = 0,
          count = 0;

        polygon.forEach(ring => {
          ring.forEach(([x, y]) => {
            totalX += x;
            totalY += y;
            count++;
          });
        });

        if (count === 0) {
          return null;
        }

        return { lng: totalY / count, lat: totalX / count };
      }
    }

    return null;
  }

  public getBoundsFromGoogleMapsDrawings(features: Array<google.maps.Marker | google.maps.Polygon>): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();

    features.forEach(drawing => {
      if (drawing instanceof google.maps.Marker) {
        bounds.extend(drawing.getPosition());
      }

      if (drawing instanceof google.maps.Polygon) {
        drawing.getPath().forEach(x => bounds.extend(x));
      }
    });

    return bounds;
  }

  public drawPolyline(map: google.maps.Map, path: google.maps.LatLng[], options: google.maps.PolylineOptions): google.maps.Polyline {
    const polyline = new google.maps.Polyline({
      ...options,
      path: path
    });
    polyline.setMap(map);
    this.polylines.push(polyline);
    return polyline;
  }

  public createCustomMarkerIconWithNumber(
    number: number | null,
    color: string | null = null,
    showX = false,
    fontSizeOverride?: number,
    size: MarkerSize = MarkerSize.Normal,
    shape: MarkerShape = MarkerShape.Pin
  ): CanvasMarkerIcon {
    if (shape === MarkerShape.Pin) {
      return this.createPinMarkerIcon(number, color, showX, fontSizeOverride, size);
    }
    return this.createCircleMarkerIcon(number, color, showX, fontSizeOverride, size);
  }

  public addWsmGeoPortalLayer(map: google.maps.Map, minZoom = 17, maxZoom = 21) {
    const TILE_SIZE = 256;
    const ORIGIN_SHIFT = 20037508.342789244;
    const INITIAL_RESOLUTION = 156543.03392804097;

    if (this.wmsLayer) {
      return;
    }

    this.wmsLayer = new google.maps.ImageMapType({
      getTileUrl: (coord, zoom) => {
        if (zoom < minZoom || zoom > maxZoom) {
          return '';
        }

        this.ngZone.run(() => {
          if (!this.isWsmLayerTilesLoading.getValue()) {
            this.isWsmLayerTilesLoading.next(true);
          }
        });
        const z = Math.pow(2, zoom);

        // Calculate the Tile Bounds in EPSG:3857
        const minx3857 = (coord.x * TILE_SIZE * INITIAL_RESOLUTION) / z - ORIGIN_SHIFT;
        const maxx3857 = ((coord.x + 1) * TILE_SIZE * INITIAL_RESOLUTION) / z - ORIGIN_SHIFT;
        const miny3857 = ORIGIN_SHIFT - ((coord.y + 1) * TILE_SIZE * INITIAL_RESOLUTION) / z;
        const maxy3857 = ORIGIN_SHIFT - (coord.y * TILE_SIZE * INITIAL_RESOLUTION) / z;

        const bbox = [minx3857, miny3857, maxx3857, maxy3857].join(',');

        return `${this.GEOPORTAL_TILES_URL}?service=WMS&version=1.1.0&request=GetMap&layers=dzialki,numery_dzialek&styles=&bbox=${bbox}&width=256&height=256&srs=EPSG:3857&format=image/png&transparent=true`;
      },
      tileSize: new google.maps.Size(TILE_SIZE, TILE_SIZE),
      opacity: 0.9,
      name: 'KrajowaIntegracjaEwidencjiGruntow'
    });

    map.overlayMapTypes.insertAt(0, this.wmsLayer);
    this.monitorTileLoading(this.wmsLayer);
  }

  public removeWsmGeoPortalLayer(map: google.maps.Map) {
    if (!this.wmsLayer) {
      return;
    }

    const index = map.overlayMapTypes.getArray().indexOf(this.wmsLayer);
    if (index === -1) {
      return;
    }

    this.ngZone.run(() => {
      map.overlayMapTypes.removeAt(index);
      this.wmsLayer = undefined;

      if (this.tilesLoadedListener) {
        google.maps.event.removeListener(this.tilesLoadedListener);
        this.tilesLoadedListener = null;
      }

      this.isWsmLayerTilesLoading.next(false);
    });
  }

  private monitorTileLoading(layer: google.maps.ImageMapType) {
    this.tilesLoadedListener = google.maps.event.addListener(layer, 'tilesloaded', () => {
      this.ngZone.run(() => {
        this.isWsmLayerTilesLoading.next(false);
      });
    });
  }

  private getPolygonCoordinates(polygon: google.maps.Polygon): Position[][] {
    return polygon
      .getPaths()
      .getArray()
      .map(path => {
        return path.getArray().map(x => {
          return [x.lng(), x.lat()];
        });
      });
  }

  // ------------------------ PIN ------------------------

  private createPinMarkerIcon(
    number: number | null,
    color: string | null,
    showX: boolean,
    fontSizeOverride: number | undefined,
    size: MarkerSize
  ): CanvasMarkerIcon {
    const cfg = MarkerSizeMap[size];
    const canvas = this.prepareCanvas(cfg.width, cfg.height);
    const ctx = canvas.getContext('2d');

    const fontSize = fontSizeOverride ? fontSizeOverride : cfg.fontSize;

    this.drawTeardrop(ctx, cfg.width, cfg.height);
    this.drawInnerCircleAndNumber(ctx, number, color, showX, fontSize, cfg.circleCenterX, cfg.circleCenterY, cfg.circleRadius);

    return {
      icon: {
        url: canvas.toDataURL(),
        anchor: new google.maps.Point(cfg.width / 2, cfg.height),
        scaledSize: new google.maps.Size(cfg.width, cfg.height)
      }
    };
  }

  // ------------------------ CIRCLE ------------------------

  private createCircleMarkerIcon(
    number: number | null,
    color: string | null,
    showX: boolean,
    fontSizeOverride: number | undefined,
    size: MarkerSize
  ): CanvasMarkerIcon {
    const cfg = CircleMarkerSizeMap[size];
    const padding = cfg.padding ? cfg.padding : 4;
    const cw = cfg.radius * 2 + padding;
    const ch = cw;

    const canvas = this.prepareCanvas(cw, ch);
    const ctx = canvas.getContext('2d');

    const cx = cw / 2;
    const cy = ch / 2;
    const rOuter = cfg.radius;
    const rInner = rOuter * 0.65;

    this.drawCircleMarker(ctx, cx, cy, rOuter, rInner, number, color, showX, fontSizeOverride ? fontSizeOverride : cfg.fontSize);

    return {
      icon: {
        url: canvas.toDataURL(),
        anchor: new google.maps.Point(cx, cy),
        scaledSize: new google.maps.Size(cw, ch)
      },
      shape: {
        type: 'circle',
        coords: [Math.round(cx), Math.round(cy), Math.round(rOuter)]
      }
    };
  }

  // ------------------------ CANVAS UTILS ------------------------

  private prepareCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  // ------------------------ DRAWING ------------------------

  private drawTeardrop(ctx: CanvasRenderingContext2D, width: number, height: number) {
    const xmul = width / 334;
    const ymul = height / 258;

    ctx.beginPath();
    ctx.moveTo(247 * xmul, 108 * ymul);
    ctx.bezierCurveTo(242 * xmul, 44 * ymul, 190 * xmul, 33 * ymul, 170 * xmul, 33 * ymul);
    ctx.bezierCurveTo(150 * xmul, 33 * ymul, 97 * xmul, 45 * ymul, 95 * xmul, 109 * ymul);
    ctx.bezierCurveTo(94.3 * xmul, 129 * ymul, 108 * xmul, 185 * ymul, 171 * xmul, 227 * ymul);
    ctx.bezierCurveTo(230 * xmul, 185 * ymul, 247 * xmul, 132 * ymul, 247 * xmul, 109 * ymul);
    ctx.closePath();
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  private drawInnerCircleAndNumber(
    ctx: CanvasRenderingContext2D,
    number: number | null,
    color: string | null,
    showX: boolean,
    fontSize: number,
    circleCenterX: number,
    circleCenterY: number,
    circleRadius: number
  ) {
    const fill = color ? color : this.defaultGradient(ctx, circleCenterX, circleCenterY, circleRadius);
    ctx.beginPath();
    ctx.arc(circleCenterX, circleCenterY, circleRadius, 0, 2 * Math.PI);
    ctx.fillStyle = fill;
    ctx.fill();

    this.drawMarkerText(ctx, number, showX, fontSize, circleCenterX, circleCenterY);
  }

  private drawCircleMarker(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    rOuter: number,
    rInner: number,
    number: number | null,
    color: string | null,
    showX: boolean,
    fontSize: number
  ) {
    // outer
    ctx.beginPath();
    ctx.arc(cx, cy, rOuter, 0, 2 * Math.PI);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.lineWidth = Math.max(1, Math.round(rOuter * 0.12));
    ctx.strokeStyle = 'rgba(0,0,0,0.08)';
    ctx.stroke();

    // inner
    const fill = color ? color : this.defaultGradient(ctx, cx, cy, rInner);
    ctx.beginPath();
    ctx.arc(cx, cy, rInner, 0, 2 * Math.PI);
    ctx.fillStyle = fill;
    ctx.fill();

    this.drawMarkerText(ctx, number, showX, fontSize, cx, cy);
  }

  private drawMarkerText(ctx: CanvasRenderingContext2D, number: number | null, showX: boolean, fontSize: number, x: number, y: number) {
    ctx.font = `bold ${fontSize}px Inter`;
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (showX) ctx.fillText('x', x, y);
    else if (number !== null && number >= 0) ctx.fillText(number.toString(), x, y);
  }

  private defaultGradient(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): CanvasGradient {
    const g = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
    g.addColorStop(0, '#34a5ad');
    g.addColorStop(1, '#096870');
    return g;
  }
}
